import React from 'react';
import { useContext, useEffect, useCallback } from "react";
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import useAxios from 'axios-hooks';
import { Language } from "../languages"
import { LanguageContext, ComplexityOverride } from "../Helpers/Context";

export default function ComplexityDetail() {
    const { language, dictionary, complexity, complexityOverride, setComplexityOverride, officeId } = useContext(LanguageContext);
    const [hasChange, setHasChange] = React.useState(false);
    const [isDefault, setIsDefault] = React.useState(true);
    const [newOverride, setNewOverride] = React.useState<ComplexityOverride>({
        officeId: 0,
        billingId: 0,
        amount: 0,
        rowStatus: "A"
    });

    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        let newValue = parseFloat(event.target.value as string);
        setHasChange(newValue > 0);
        let truncValue = Math.round(newValue * 100) / 100;
        setNewOverride({ ...newOverride, amount: truncValue });
    };

    const cancelChange = () => {
        setInitialValues();
    };

    async function useDefault() {
        if (complexity && complexityOverride) {
            await executePostDelete();
            setComplexityOverride( { ...complexityOverride, rowStatus: "D"});
            setHasChange(false);
        }
    };

    async function saveChange() {
        if (complexity) {
            await executePostActive();
            setComplexityOverride(newOverride);
            setHasChange(false);
        }
    };

    const setInitialValues = useCallback(() => {
        let initOverride: ComplexityOverride =
        {
            officeId: officeId,
            billingId: complexity?.billingId ?? 0,
            amount: 0,
            rowStatus: "A"
        }

        if (complexityOverride && complexityOverride.rowStatus === "A") {
            initOverride.amount = complexityOverride.amount;
        }
        else {
            initOverride.amount = complexity?.defaultRate ?? 0;
        }
        setNewOverride(initOverride);
        setHasChange(false);
        setIsDefault(complexityOverride?.rowStatus !== "A");
    }, [complexity, complexityOverride, officeId]);

    useEffect(() => {
        setInitialValues();
    }, [complexity, complexityOverride, setInitialValues]);

    const [, executePostActive] = useAxios(
        {
            url: `Complexity/Override?officeId=${newOverride.officeId}&billingId=${newOverride.billingId}&amount=${newOverride.amount}&status=A`,
            method: "POST"
        },
        { manual: true }
    );

    const [, executePostDelete] = useAxios(
        {
            url: `Complexity/Override?officeId=${complexityOverride?.officeId}&billingId=${complexityOverride?.billingId}&amount=${complexityOverride?.amount}&status=D`,
            method: "POST"
        },
        { manual: true }
    );

    return (
        <React.Fragment>
            <Typography variant="h6" color="primary">
                {dictionary.complexityDetailTitle}
            </Typography>
            <FormControl>
                <Grid container spacing={1} alignItems="flex-start" direction="column">
                    <Grid item xs={12} >
                        <Typography variant="subtitle1" display="inline" color="primary">
                            {dictionary.complexityDetailComplexity}:
                        </Typography>
                        {language === Language.English ? complexity?.description : complexity?.descriptionFr}
                    </Grid>
                    <Grid item xs={12} >
                        <Typography variant="subtitle1" display="inline" color="primary">
                            {dictionary.complexityDetailDefaultAmount}:
                        </Typography>
                        ${complexity?.defaultRate.toFixed(2)}
                    </Grid>
                    <Grid item xs={12} >
                        <Typography variant="subtitle1" display="inline" color="primary">
                            {dictionary.complexityDetailCurrentAmount}:
                        </Typography>
                        <TextField type='number' onChange={handleChange} value={newOverride.amount} />
                        <Typography variant="body2" display="inline">
                            ({isDefault ? dictionary.complexityDefault : dictionary.complexityCustom})
                        </Typography>
                    </Grid>
                    <Grid item xs={12} ></Grid>
                </Grid>
                <Grid container spacing={1}>
                    <Grid item xs="auto" >
                        <Button variant="contained" color="primary" size="small" disabled={!hasChange} onClick={saveChange}>{dictionary.save}</Button>
                    </Grid>
                    <Grid item xs="auto" >
                        <Button variant="contained" color="primary" size="small" disabled={!hasChange} onClick={cancelChange}>{dictionary.cancel}</Button>
                    </Grid>
                    <Grid item xs ></Grid>
                    <Grid item xs="auto" >
                        <Button variant="contained" color="primary" size="small" disabled={isDefault} onClick={useDefault}>{dictionary.useDefault}</Button>
                    </Grid>
                </Grid>
            </FormControl>
        </React.Fragment>
    );
}